/**
 * Sorts one container's status entries into what the manager reads under the container itself
 * and what belongs under each of its splits.
 *
 * A status shows under a split only when it was recorded on one of this container's splits.
 * A status with no split, or one pointing at another container's split, is a container-level
 * status: it stays in the container's list in date order instead of being pushed below the
 * split headers.
 *
 * @param {object[]} statusMsg  status entries already filtered and sorted for the panel
 * @param {number} cntrId       the container being rendered
 * @param {object[]} aliasItems the splits that hang under this container
 * @returns {{ containerItems: object[], aliasGroups: { alias: object, items: object[] }[], hasEntries: boolean }}
 */
export const groupContainerStatusMessages = (statusMsg, cntrId, aliasItems = []) => {
    const ownEntries = (statusMsg ?? []).filter((item) => item.child_id === cntrId);
    const splits = Array.isArray(aliasItems) ? aliasItems : [];
    const aliasIds = new Set(splits.map((alias) => alias.id));

    const containerItems = ownEntries.filter((item) => !aliasIds.has(item.alias_id));
    const aliasGroups = splits.map((alias) => ({
        alias,
        items: ownEntries.filter((item) => item.alias_id === alias.id),
    }));

    return { containerItems, aliasGroups, hasEntries: ownEntries.length > 0 };
};

/**
 * The heading the manager sees above a split's status entries.
 *
 * A split keeps its statuses even after the manager clears its name in the Split Container
 * Cargo grid. Without a heading those entries look like container entries out of date order,
 * so a split is always introduced as "Split (<name>)", or "Split (no name)" when the name is empty.
 */
export const splitHeaderLabel = (alias) => {
    const name = typeof alias?.alias_name === 'string' ? alias.alias_name.trim() : '';
    return `Split (${name || 'no name'})`;
};
